import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Facebook } from 'lucide-react';
import axios from 'axios';
import PoweredByBranding from './PoweredByBranding';
import BrandLogo from './BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_ICONS = {
  linkedin: Linkedin,
  facebook: Facebook,
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [socialChannels, setSocialChannels] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cms/social-media/active`)
      .then(res => {
        const channels = (res.data.channels || []).map(ch => {
          if (ch.icon_url && ch.icon_url.startsWith('/api/')) {
            ch.icon_url = `${BACKEND_URL}${ch.icon_url}`;
          }
          return ch;
        });
        setSocialChannels(channels);
      })
      .catch(() => {
        setSocialChannels([
          { channel_id: 'fallback-li', name: 'LinkedIn', url: 'https://www.linkedin.com/company/exlva', icon_url: null },
          { channel_id: 'fallback-fb', name: 'Facebook', url: 'https://www.facebook.com/exlva', icon_url: null },
        ]);
      });
  }, []);

  const renderSocialIcon = (channel) => {
    if (channel.icon_url) {
      return <img src={channel.icon_url} alt={channel.name} className="w-4 h-4 object-contain" />;
    }
    const key = channel.name.toLowerCase();
    const FallbackIcon = FALLBACK_ICONS[key];
    if (FallbackIcon) return <FallbackIcon className="w-4 h-4" />;
    return <span className="text-xs font-bold">{channel.name.charAt(0).toUpperCase()}</span>;
  };

  const channelsToShow = socialChannels === null
    ? [
        { channel_id: 'default-li', name: 'LinkedIn', url: 'https://www.linkedin.com/company/exlva', icon_url: null },
        { channel_id: 'default-fb', name: 'Facebook', url: 'https://www.facebook.com/exlva', icon_url: null },
      ]
    : socialChannels;

  return (
    <footer className="bg-[#0A1628] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center md:items-start">
          {/* Left Subchapter - Logo and Contact Info */}
          <div className="space-y-3 md:max-w-md text-center md:text-left">
            {/* Logo */}
            <div className="flex justify-center md:justify-start">
              <BrandLogo className="w-auto object-contain brightness-200" style={{ height: '36px' }} />
            </div>

            {/* Office Address */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-1 flex items-center justify-center md:justify-start">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Office:
              </h3>
              <address className="text-gray-300 text-xs not-italic leading-snug">
                24, Wellington St, Richmond Town,<br />
                Bengaluru, Karnataka 560025
              </address>
              <p className="text-gray-300 text-xs mt-1">Phone: +91 8010088285</p>
            </div>

            {/* Email */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-1 flex items-center justify-center md:justify-start">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email:
              </h3>
              <a href="mailto:support.hospital@eptral.com" className="text-gray-300 text-xs hover:text-blue-400 transition-colors">
                support.hospital@eptral.com
              </a>
            </div>

            {/* Social Media - CMS Driven */}
            {channelsToShow.length > 0 && (
              <div className="flex space-x-3 justify-center md:justify-start">
                {channelsToShow.map(channel => (
                  <a
                    key={channel.channel_id}
                    href={channel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center transition-colors"
                    aria-label={channel.name}
                  >
                    {renderSocialIcon(channel)}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Right Subchapter - Links */}
          <div className="flex gap-6 md:gap-12 mt-4 md:mt-0">
            {/* Vertical Divider */}
            <div className="hidden md:block">
              <div className="h-full w-px bg-gray-700"></div>
            </div>

            {/* Navigation Links */}
            <div className="space-y-2 text-center md:text-right w-full md:w-auto">
              <Link
                to="/"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                Home
              </Link>
              <Link
                to="/solutions"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                The Solution
              </Link>
              <Link
                to="/pricing"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                Contact us
              </Link>
              <Link
                to="/request-demo"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                Request demo
              </Link>
              <Link
                to="/signin"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                Sign-in
              </Link>
              <Link
                to="/signup"
                className="block text-gray-300 hover:text-white transition-colors text-sm"
              >
                Get started
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Copyright and Legal */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            {/* Copyright and Powered By */}
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4 text-sm text-gray-400 text-center">
              <p>&copy; {currentYear} Eptral.Hospital. All rights reserved.</p>
              <span className="hidden md:inline text-gray-600">|</span>
              <PoweredByBranding
                className="max-w-[180px] text-gray-400"
                linkClassName="hover:text-white transition-colors max-w-[92px]"
                textClassName="text-red-500 font-semibold"
                logoClassName="max-h-5 max-w-[90px]"
              />
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <Link
                to="/privacy-policy"
                className="hover:text-white transition-colors"
              >
                Privacy policy
              </Link>
              <Link
                to="/terms-and-conditions"
                className="hover:text-white transition-colors"
              >
                Terms & conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
